// Core
import React from 'react';

// Libraries
import _ from 'lodash';
import { useTheme } from 'styled-components';
import { useTranslation } from 'react-i18next';

// Plugins
import {
  DEVICE_COMMUNICATION_THRESHOLD,
  isEquipmentRunning,
  overallStatusToTagType,
  overallStatusToText,
} from '../../../../../../plugins/crusher';
import { MOTOR_RPM_RANGE, MOTOR_AMPS_RANGE } from '../../../../../../plugins/thresholds';
import {
  Paragraph,
  ParagraphSize,
  ParagraphWeight,
} from '../../../../../../layout/elements/Paragraph';

// Types
import { Theme } from '../../../../../../types/theme';

// Components
import { Col } from '../../../../../../layout/elements/Col';
import { ModuleHeader } from '../../../../../../layout/elements/ModuleHeader';
import { Row } from '../../../../../../layout/elements/Row';
import { Speedometer } from '../../../../../../layout/elements/Speedometer';
import { Tag } from '../../../../../../layout/elements/Tag';

// View
import {
  MotorAmpsContainer,
  MotorRpmContainer,
  MotorStatusContainer,
  OverallStatusContainer,
  SpeedometersContainer,
} from './MotorStatus.style';
import { MotorStatusProps } from './MotorStatus.types';

function MotorStatus({
  className,
  onOpenDrawer,
  unitData,
  edgeStatus,
  testId,
}: MotorStatusProps): JSX.Element {
  // Dependencies
  const { t } = useTranslation();
  const theme: Theme = useTheme();

  return (
    <MotorStatusContainer
      data-testid={testId}
      className={className}
    >
      <Row>
        <Col>
          <ModuleHeader
            onClick={onOpenDrawer}
            title={t('view.crusher.crusherMotor.title')}
            testId={`${testId}-module-header`}
          />
        </Col>
      </Row>
      <Row>
        <Col cols={24}>
          <OverallStatusContainer data-testid="motor-status-overall-container">
            <Paragraph
              testId="motor-status-overall-paragraph"
              size={ParagraphSize.sm}
              weight={ParagraphWeight.medium}
              color={theme.color.crusherDashboard.statusTitleColor}
            >
              {t('view.crusher.overallStatus')}
            </Paragraph>
            <Tag
              text={overallStatusToText(unitData?.crusherMotorOverallStatus)}
              type={overallStatusToTagType(
                unitData?.crusherMotorOverallStatus,
                edgeStatus ? edgeStatus.edgeStatus : null,
                unitData?.timestamp,
              )}
            />
          </OverallStatusContainer>
        </Col>
      </Row>
      <SpeedometersContainer>
        {unitData?.crusherSpeedMonitorEnable && (
          <MotorRpmContainer cols={12}>
            <Speedometer
              className="speedometer"
              minValue={_.isNil(unitData?.crusherMotorRpm)
                ? null : MOTOR_RPM_RANGE.minValue}
              maxValue={_.isNil(unitData?.crusherMotorRpm)
                ? null : MOTOR_RPM_RANGE.maxValue}
              value={unitData?.crusherMotorRpm}
              unit="rpm"
              thresholds={{
                lowWarning: unitData?.spBeltSlipAlarmRpm,
              }}
              title={`${t('view.crusher.crusherMotor.motorRmp')}`}
              running={
                isEquipmentRunning(
                  edgeStatus,
                  unitData?.timestamp,
                  DEVICE_COMMUNICATION_THRESHOLD,
                )
              }
            />
          </MotorRpmContainer>
        )}
        <MotorAmpsContainer data-testid="motor-amps-container">
          <Speedometer
            testId="motor-amps-speedometer"
            className="speedometer"
            minValue={_.isNil(unitData?.crusherMotorAmps)
              ? null : MOTOR_AMPS_RANGE.minValue}
            maxValue={_.isNil(unitData?.crusherMotorAmps)
              ? null : MOTOR_AMPS_RANGE.maxValue}
            value={unitData?.crusherMotorAmps}
            unit="A"
            thresholds={{
              highWarning: unitData?.spCrusherMotorFullLoadAmps,
            }}
            title={`${t('view.crusher.crusherMotor.motorAmps')}`}
            running={
              isEquipmentRunning(
                edgeStatus,
                unitData?.timestamp,
                DEVICE_COMMUNICATION_THRESHOLD,
              )
            }
          />
        </MotorAmpsContainer>
      </SpeedometersContainer>
    </MotorStatusContainer>
  );
}

export { MotorStatus };
